import dayjs from 'dayjs';

import type {
  CycleStatus,
  LeaderboardDefinition,
  LeaderboardDefinitionId,
  LeaderboardKind,
  LeaderboardPeriod,
  LeaderboardScope,
  LeaderboardTier,
  LeaderboardTierDraft,
  LeaderboardTierList,
  LeaderboardTierListResponse,
  ReplaceLeaderboardTiersPayload,
  SettlementEntry,
  SettlementPayoutStatus,
  SettlementSummary,
} from './types';

export function formatDateTime(value?: string | null) {
  if (!value) return '–';
  const parsed = dayjs(value);
  if (!parsed.isValid()) return '–';
  return parsed.format('YYYY-MM-DD HH:mm:ss');
}

type DefaultDefinitionEntry = {
  id: string;
  kind: LeaderboardKind;
  scope: LeaderboardScope;
  minThreshold: string;
  description: string;
};

export const LEADERBOARD_DEFINITION_DEFAULTS: DefaultDefinitionEntry[] = [
  {
    id: 'prediction-volume',
    kind: 'volume',
    scope: 'prediction',
    minThreshold: '1000',
    description: 'Prediction Volume',
  },
  {
    id: 'prediction-profit',
    kind: 'profit',
    scope: 'prediction',
    minThreshold: '100',
    description: 'Prediction Profit',
  },
  {
    id: 'perps-volume',
    kind: 'volume',
    scope: 'perps',
    minThreshold: '1000',
    description: 'Perps Volume',
  },
  {
    id: 'perps-profit',
    kind: 'profit',
    scope: 'perps',
    minThreshold: '100',
    description: 'Perps Profit',
  },
  {
    id: 'grid-volume',
    kind: 'volume',
    scope: 'grid',
    minThreshold: '1000',
    description: 'Grid Volume',
  },
  {
    id: 'grid-profit',
    kind: 'profit',
    scope: 'grid',
    minThreshold: '100',
    description: 'Grid Profit',
  },
];

export const LEADERBOARD_DEFINITION_IDS: LeaderboardDefinitionId[] =
  LEADERBOARD_DEFINITION_DEFAULTS.map((item) => item.id);

export const LEADERBOARD_KIND_OPTIONS: Array<{
  value: LeaderboardKind;
  labelKey: string;
}> = [
  { value: 'volume', labelKey: 'kind.volume' },
  { value: 'profit', labelKey: 'kind.profit' },
];

export const LEADERBOARD_SCOPE_OPTIONS: Array<{
  value: LeaderboardScope;
  labelKey: string;
}> = [
  { value: 'prediction', labelKey: 'scope.prediction' },
  { value: 'perps', labelKey: 'scope.perps' },
  { value: 'grid', labelKey: 'scope.grid' },
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function ensureArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? value : [];
}

export function toLocalDateTimeInput(iso?: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

export function fromLocalDateTimeInput(local: string): string {
  if (!local) return '';
  const date = new Date(local);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString();
}

export function toDateInput(iso?: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

export function fromDateInputStartOfDay(local: string): string {
  if (!local) return '';
  const [y, m, d] = local.split('-').map(Number);
  if (!y || !m || !d) return '';
  const ms = Date.UTC(y, m - 1, d, 0, 0, 0, 0);
  if (Number.isNaN(ms)) return '';
  return new Date(ms).toISOString();
}

export function fromDateInputEndOfDay(local: string): string {
  if (!local) return '';
  const [y, m, d] = local.split('-').map(Number);
  if (!y || !m || !d) return '';
  const ms = Date.UTC(y, m - 1, d, 23, 59, 59, 0);
  if (Number.isNaN(ms)) return '';
  return new Date(ms).toISOString();
}

const CYCLE_STATUS_VALUES: CycleStatus[] = ['ended', 'active', 'upcoming'];

function normalizeCycleStatus(value: unknown): CycleStatus {
  return CYCLE_STATUS_VALUES.includes(value as CycleStatus) ? (value as CycleStatus) : 'upcoming';
}

function normalizePeriod(raw: unknown): LeaderboardPeriod | null {
  if (!isRecord(raw)) return null;

  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? ''),
    startsAt: String(raw.startsAt ?? raw.starts_at ?? ''),
    endsAt: String(raw.endsAt ?? raw.ends_at ?? ''),
    status: normalizeCycleStatus(raw.status),
    frozenAt:
      raw.frozenAt === null || raw.frozen_at === null
        ? null
        : raw.frozenAt
          ? String(raw.frozenAt)
          : raw.frozen_at
            ? String(raw.frozen_at)
            : null,
    configVersion: Number(raw.configVersion ?? raw.config_version ?? 0) || 0,
    updatedBy: String(raw.updatedBy ?? raw.updated_by ?? ''),
  };
}

const PAYOUT_STATUS_VALUES: Exclude<SettlementPayoutStatus, null>[] = [
  'pending',
  'processing',
  'sent',
  'failed',
  'none',
];

function normalizePayoutStatus(value: unknown): SettlementPayoutStatus {
  if (value === null || value === undefined || value === '') return 'none';
  return PAYOUT_STATUS_VALUES.includes(value as Exclude<SettlementPayoutStatus, null>)
    ? (value as SettlementPayoutStatus)
    : 'none';
}

function normalizeSettlementEntry(raw: unknown): SettlementEntry | null {
  if (!isRecord(raw)) return null;

  return {
    id: String(raw.id ?? ''),
    rank: Number(raw.rank ?? 0) || 0,
    address: String(raw.address ?? ''),
    value: String(raw.value ?? '0'),
    amountUsdc: String(raw.amountUsdc ?? raw.amount_usdc ?? '0'),
    autoPayout:
      typeof raw.autoPayout === 'boolean'
        ? raw.autoPayout
        : typeof raw.auto_payout === 'boolean'
          ? raw.auto_payout
          : false,
    payoutStatus: normalizePayoutStatus(raw.payoutStatus ?? raw.payout_status),
    sentAt:
      raw.sentAt === null || raw.sent_at === null
        ? null
        : raw.sentAt
          ? String(raw.sentAt)
          : raw.sent_at
            ? String(raw.sent_at)
            : null,
  };
}

export function normalizeSettlement(
  raw: unknown,
  fallbackPeriodId: string,
  fallbackDefinitionId: LeaderboardDefinitionId
): SettlementSummary {
  const source = isRecord(raw) && isRecord(raw.data) ? raw.data : raw;

  if (!isRecord(source)) {
    return {
      periodId: fallbackPeriodId,
      definitionId: fallbackDefinitionId,
      winnerCount: 0,
      paidCount: 0,
      totalDue: '0',
      paidAmount: '0',
      entries: [],
    };
  }

  return {
    periodId: String(source.periodId ?? source.period_id ?? fallbackPeriodId),
    definitionId: String(source.definitionId ?? source.definition_id ?? fallbackDefinitionId),
    winnerCount: Number(source.winnerCount ?? source.winner_count ?? 0) || 0,
    paidCount: Number(source.paidCount ?? source.paid_count ?? 0) || 0,
    totalDue: String(source.totalDue ?? source.total_due ?? '0'),
    paidAmount: String(source.paidAmount ?? source.paid_amount ?? '0'),
    entries: ensureArray<unknown>(source.entries)
      .map(normalizeSettlementEntry)
      .filter((item): item is SettlementEntry => Boolean(item)),
  };
}

export function normalizePeriods(raw: unknown): LeaderboardPeriod[] {
  const source = isRecord(raw) && isRecord(raw.data) ? raw.data : raw;

  const list = Array.isArray(source)
    ? source
    : isRecord(source) && Array.isArray(source.periods)
      ? source.periods
      : isRecord(source) && Array.isArray(source.items)
        ? source.items
        : [];

  return list.map(normalizePeriod).filter((item): item is LeaderboardPeriod => Boolean(item));
}

export function toNumber(value?: unknown) {
  const num = Number(value ?? 0);

  return Number.isFinite(num) ? num : 0;
}

export function formatUsdc(value?: unknown) {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  }).format(toNumber(value));
}

export function getDefinitionKind(id: LeaderboardDefinitionId): LeaderboardKind {
  return id.endsWith('profit') ? 'profit' : 'volume';
}

export function getDefinitionScope(id: LeaderboardDefinitionId): LeaderboardScope {
  if (id.startsWith('perps')) return 'perps';
  if (id.startsWith('grid')) return 'grid';
  return 'prediction';
}

export function getDefaultDefinition(id: LeaderboardDefinitionId): LeaderboardDefinition {
  const preset = LEADERBOARD_DEFINITION_DEFAULTS.find((item) => item.id === id);
  const kind = preset?.kind ?? getDefinitionKind(id);
  const scope = preset?.scope ?? getDefinitionScope(id);

  return {
    id,
    kind,
    scope,
    minThreshold: preset?.minThreshold ?? '0',
    description: preset?.description ?? `${scope} / ${kind}`,
    tierCount: 0,
    totalPoolUsdc: '0',
  };
}

export function getDefaultDefinitions(): LeaderboardDefinition[] {
  return LEADERBOARD_DEFINITION_DEFAULTS.map((item) => getDefaultDefinition(item.id));
}

function normalizeKind(value: unknown, fallback: LeaderboardKind): LeaderboardKind {
  return value === 'profit' || value === 'volume' ? value : fallback;
}

function normalizeScope(value: unknown, fallback: LeaderboardScope): LeaderboardScope {
  return value === 'prediction' || value === 'perps' || value === 'grid' ? value : fallback;
}

export function normalizeDefinition(
  raw: unknown,
  fallbackId?: LeaderboardDefinitionId
): LeaderboardDefinition {
  const id = isRecord(raw) && raw.id ? String(raw.id) : (fallbackId ?? 'prediction-volume');
  const fallback = getDefaultDefinition(id);

  if (!isRecord(raw)) {
    return fallback;
  }

  return {
    ...fallback,
    id,
    kind: normalizeKind(raw.kind, fallback.kind),
    scope: normalizeScope(raw.scope, fallback.scope),
    minThreshold: String(raw.minThreshold ?? raw.min_threshold ?? fallback.minThreshold),
    description: String(raw.description ?? fallback.description),
    tierCount: toNumber(raw.tierCount ?? raw.tier_count ?? fallback.tierCount),
    totalPoolUsdc: String(raw.totalPoolUsdc ?? raw.total_pool_usdc ?? fallback.totalPoolUsdc),
    configVersion:
      raw.configVersion === undefined && raw.config_version === undefined
        ? undefined
        : toNumber(raw.configVersion ?? raw.config_version),
    updatedBy:
      raw.updatedBy !== undefined
        ? String(raw.updatedBy)
        : raw.updated_by !== undefined
          ? String(raw.updated_by)
          : undefined,
    updatedAt:
      raw.updatedAt !== undefined
        ? String(raw.updatedAt)
        : raw.updated_at !== undefined
          ? String(raw.updated_at)
          : undefined,
  };
}

export function normalizeDefinitions(raw: unknown): LeaderboardDefinition[] {
  if (Array.isArray(raw)) {
    return fillMissingDefinitions(raw.map((item) => normalizeDefinition(item)));
  }

  if (!isRecord(raw)) {
    return getDefaultDefinitions();
  }

  const source =
    raw.definitions ??
    raw.items ??
    raw.list ??
    raw.rows ??
    (Array.isArray(raw.data) ? raw.data : undefined) ??
    (isRecord(raw.data) ? raw.data.definitions : undefined);

  if (!Array.isArray(source)) {
    return getDefaultDefinitions();
  }

  return fillMissingDefinitions(source.map((item) => normalizeDefinition(item)));
}

function fillMissingDefinitions(definitions: LeaderboardDefinition[]) {
  const map = new Map<LeaderboardDefinitionId, LeaderboardDefinition>();

  definitions.forEach((item) => {
    map.set(item.id, item);
  });

  return LEADERBOARD_DEFINITION_IDS.map((id) => map.get(id) ?? getDefaultDefinition(id));
}

export function normalizeTier(raw: unknown): LeaderboardTier {
  if (!isRecord(raw)) {
    return {
      rangeFrom: 1,
      rangeTo: 10,
      rewardPerRank: '0',
      autoPayout: true,
    };
  }

  return {
    id: raw.id === undefined ? undefined : String(raw.id),
    rangeFrom: toNumber(raw.rangeFrom ?? raw.range_from),
    rangeTo: toNumber(raw.rangeTo ?? raw.range_to),
    rewardPerRank: String(raw.rewardPerRank ?? raw.reward_per_rank ?? '0'),
    tierTotalUsdc:
      raw.tierTotalUsdc === undefined && raw.tier_total_usdc === undefined
        ? undefined
        : String(raw.tierTotalUsdc ?? raw.tier_total_usdc),
    autoPayout:
      typeof raw.autoPayout === 'boolean'
        ? raw.autoPayout
        : typeof raw.auto_payout === 'boolean'
          ? raw.auto_payout
          : true,
    sortOrder:
      raw.sortOrder === undefined && raw.sort_order === undefined
        ? undefined
        : toNumber(raw.sortOrder ?? raw.sort_order),
  };
}

export function normalizeTierList(
  raw: LeaderboardTierListResponse | unknown,
  fallbackId: LeaderboardDefinitionId
): LeaderboardTierList {
  if (!raw) {
    return {
      definitionId: fallbackId,
      totalPoolUsdc: '0',
      tiers: [],
    };
  }

  const source =
    isRecord(raw) && isRecord(raw.tierList)
      ? raw.tierList
      : isRecord(raw) && isRecord(raw.data) && isRecord(raw.data.tierList)
        ? raw.data.tierList
        : isRecord(raw) && isRecord(raw.data)
          ? raw.data
          : raw;

  if (!isRecord(source)) {
    return {
      definitionId: fallbackId,
      totalPoolUsdc: '0',
      tiers: [],
    };
  }

  const rawDefinitionId = source.definitionId ?? source.definition_id;
  const definitionId = rawDefinitionId ? String(rawDefinitionId) : fallbackId;

  return {
    definitionId,
    totalPoolUsdc: String(source.totalPoolUsdc ?? source.total_pool_usdc ?? '0'),
    tiers: ensureArray<unknown>(source.tiers).map(normalizeTier),
  };
}

export function toDraftRows(tiers: LeaderboardTier[]): LeaderboardTierDraft[] {
  return tiers.map((tier, index) => ({
    ...tier,
    localId: tier.id ? `server-${tier.id}` : `local-${Date.now()}-${index}`,
  }));
}

export function createEmptyTier(prev?: LeaderboardTierDraft): LeaderboardTierDraft {
  const start = prev ? Math.max(toNumber(prev.rangeTo) + 1, 1) : 1;
  const end = start + 9;

  return {
    localId: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    rangeFrom: start,
    rangeTo: end,
    rewardPerRank: '0',
    autoPayout: true,
  };
}

export function parseRankRange(value: string) {
  const normalized = value.replace(/\s/g, '');
  const [from, to] = normalized.split('-');

  return {
    rangeFrom: toNumber(from),
    rangeTo: toNumber(to ?? from),
  };
}

export function formatRankRange(row: LeaderboardTierDraft) {
  return `${row.rangeFrom}-${row.rangeTo}`;
}

export function buildReplaceTiersPayload(
  rows: LeaderboardTierDraft[]
): ReplaceLeaderboardTiersPayload {
  return {
    tiers: rows.map((row) => ({
      rangeFrom: toNumber(row.rangeFrom),
      rangeTo: toNumber(row.rangeTo),
      rewardPerRank: String(row.rewardPerRank || '0'),
      autoPayout: Boolean(row.autoPayout),
    })),
  };
}

export function isTierRowsEqual(left: LeaderboardTierDraft[], right: LeaderboardTierDraft[]) {
  return (
    JSON.stringify(buildReplaceTiersPayload(left)) ===
    JSON.stringify(buildReplaceTiersPayload(right))
  );
}

export function getErrorMessage(error: unknown) {
  if (!error) return '';

  if (typeof error === 'string') return error;

  if (error instanceof Error) return error.message;

  if (isRecord(error)) {
    if (typeof error.message === 'string') return error.message;
    if (typeof error.error === 'string') return error.error;

    if (isRecord(error.response) && isRecord(error.response.data)) {
      const data = error.response.data;

      if (typeof data.message === 'string') return data.message;
      if (typeof data.error === 'string') return data.error;
    }
  }

  return String(error);
}
